import type { FC, PropsWithChildren } from 'react';
import { useRouter } from 'next/router';

import type { VehicleListQuery } from 'Components/Account/AccountPage.types';
import {
	VehicleListEmptyState,
	VehicleListEmptyStateVariant,
} from 'Components/Account/utilities/VehicleListEmptyState/VehicleListEmptyState';
import {
	generateObjectsArray,
	isViewEmpty,
	isViewEmptyFilters,
	isViewError,
	isViewLoading,
} from 'Components/Account/utilities/VehicleListEmptyState/VehicleListEmptyState.helpers';
import { useHandleKycStatus } from 'Utilities/hooks/useHandleKycStatus';

export type SkeletonRowProps = { id: string; index: number };

type VehicleListStateHandlerProps = Pick<VehicleListQuery, 'isPreviousData' | 'queryStatus'> &
	PropsWithChildren<{
		placeholderRowsCount: number;
		rowsCount?: number;
		skeletonRow: FC<SkeletonRowProps>;
	}>;

const VehicleListStateHandler: FC<VehicleListStateHandlerProps> = ({
	children,
	isPreviousData,
	placeholderRowsCount,
	queryStatus,
	rowsCount,
	skeletonRow: SkeletonRow,
}) => {
	const {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		query: { params, ...filtersQuery },
	} = useRouter();

	const { isKycCompleted, isKycInProgress } = useHandleKycStatus({
		source: 'VehicleListStateHandler',
	});

	const isLoading = isViewLoading(queryStatus, isPreviousData);
	const isEmpty = isViewEmpty(queryStatus, rowsCount);
	const isEmptyFilter = isViewEmptyFilters(isEmpty, filtersQuery);
	const isError = isViewError(queryStatus);

	const placeholders = generateObjectsArray(placeholderRowsCount);

	if (isLoading) {
		return (
			<>
				{placeholders.map(({ id }, index) => (
					<SkeletonRow key={id} id={id.toString()} index={index}></SkeletonRow>
				))}
			</>
		);
	}

	if (isEmpty && isKycInProgress()) {
		return <VehicleListEmptyState variant={VehicleListEmptyStateVariant.EmptyKyc} />;
	}
	if (isEmpty && isKycCompleted()) {
		return <VehicleListEmptyState variant={VehicleListEmptyStateVariant.EmptyKycSubmitted} />;
	}
	if (isEmptyFilter) {
		return <VehicleListEmptyState variant={VehicleListEmptyStateVariant.Filters} />;
	}
	if (isEmpty) {
		return <VehicleListEmptyState variant={VehicleListEmptyStateVariant.Empty} />;
	}
	if (isError) {
		return <VehicleListEmptyState variant={VehicleListEmptyStateVariant.Error} />;
	}

	return <>{children}</>;
};

export { VehicleListStateHandler };
